import React, {useCallback, useMemo} from 'react';

import {Analytics} from '../../../../services/analytics';
import {isSystemMetric} from '../../../../util/plotHelpers/prettifyMetricName';
import {globalXAxisOptions} from '../../../../util/runhelpers';
import {useKeyInfoQueryContext} from '../../../MultiRunWorkspace/KeyInfoQueryContext';
import {SettingLabel} from '../components/SettingLabel';
import {SettingSelect} from '../components/SettingSelect';
import {
  SemanticLegendSettingsWithCategoricalConfig,
  SettingSelectOptionType,
  YAxisValues,
} from '../types';
import {xAxisLabel} from '../utils';
import {DEFAULT_X_AXIS_OPTION_VALUES} from './SemanticLegendDefaults';

export type SemanticLegendKeyProps = {
  currentKey: string;
  type: YAxisValues;
  updateSetting: (
    settings: Partial<SemanticLegendSettingsWithCategoricalConfig>
  ) => void;
  configKeys: string[];
};

export const SemanticLegendKeyWithCategoricalConfig = ({
  updateSetting,
  currentKey,
  type,
  configKeys,
}: SemanticLegendKeyProps) => {
  const isConfigBasedColors =
    type === YAxisValues.Config_Categories ||
    type === YAxisValues.Config_Number;
  const metricOptions = useWorkspaceMetricOptions();
  const configKeyOptions = useWorkspaceConfigKeyOptions(configKeys);
  const keyOptions = useMemo(() => {
    const options = [...metricOptions, ...configKeyOptions];
    // If the current key is not in the options because of the currently filtered runs,
    // we need to tack it on so the dropdown is not empty.
    if (!options.some(opt => opt.value === currentKey)) {
      options.push({
        value: currentKey,
        label: xAxisLabel(currentKey),
      });
    }
    return options;
  }, [metricOptions, configKeyOptions, currentKey]);
  const keyValue = useMemo(() => {
    return keyOptions.find(opt => opt.value === currentKey);
  }, [currentKey, keyOptions]);

  const onKeyChange = useCallback(
    (
      selected: SettingSelectOptionType | null,
      configKeyOptions: {label: string; value: string}[],
      type: YAxisValues
    ) => {
      if (selected != null) {
        let settingsToUpdate: Partial<SemanticLegendSettingsWithCategoricalConfig> =
          {
            key: selected.value,
            shouldRecalculateBuckets: true,
          };
        // If the selected key is a config key, update the yAxisValue if not config based
        if (configKeyOptions.some(opt => opt.value === selected.value)) {
          settingsToUpdate = {
            ...settingsToUpdate,
            yAxisValue: !isConfigBasedColors
              ? YAxisValues.Config_Categories
              : type,
          };
        } else {
          // If the selected key is a metric and the previous selected key was a config key, default the yAxisValue to Last and the number of buckets to 4
          if (isConfigBasedColors) {
            settingsToUpdate = {
              ...settingsToUpdate,
              yAxisValue: YAxisValues.Last,
            };
          }
        }
        Analytics.track('Semantic legend key changed', {
          newValue: selected?.value,
        });
        updateSetting(settingsToUpdate);
      }
    },
    [updateSetting, isConfigBasedColors]
  );

  return (
    <div className="flex flex-col" data-test="metric-setting">
      <div className="flex w-full">
        <SettingLabel text="Key" className="ml-14 w-[100px]" />
        <div className="item-center flex w-full flex-row gap-8">
          <SettingSelect
            cacheOptions={false} // the options can be affected by the current filtered runs, so we can't cache the search results
            className="grow"
            options={keyOptions}
            value={keyValue}
            onChange={selected => onKeyChange(selected, configKeyOptions, type)}
          />
        </div>
      </div>
    </div>
  );
};

export const useWorkspaceMetricOptions = () => {
  const {keyInfoQuery} = useKeyInfoQueryContext();
  const historyKeyXAxis = useMemo(
    () => globalXAxisOptions(keyInfoQuery.historyKeyInfo),
    [keyInfoQuery.historyKeyInfo]
  );

  const metricValues = useMemo(() => {
    return DEFAULT_X_AXIS_OPTION_VALUES.concat(historyKeyXAxis)
      .filter(val => !isSystemMetric(val))
      .map(val => ({
        label: xAxisLabel(val),
        value: val,
      }));
  }, [historyKeyXAxis]);

  return metricValues;
};

export const useWorkspaceConfigKeyOptions = (configKeys: string[]) => {
  const configValues = useMemo(() => {
    return configKeys.map(key => ({
      label: configKeyLabel(key),
      value: key,
    }));
  }, [configKeys]);
  return configValues;
};

const configKeyLabel = (key: string) => {
  return key.replace('config.', '').replace('.value', '');
};
